import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Guard: once login() or setUserFromCallback() succeeds, prevent checkAuth from overwriting
  const authCompleted = useRef(false);

  const checkAuth = useCallback(async (force = false) => {
    // Don't overwrite if login/callback already authenticated (unless forced)
    if (authCompleted.current && !force) {
      setLoading(false);
      return user;
    }
    try {
      const response = await api.get('/auth/me');
      // Double-check guard in case login() completed while this was in-flight
      if (!authCompleted.current || force) {
        authCompleted.current = true;
        setUser(response.data);
        setError(null);
      }
      return response.data;
    } catch (err) {
      if (!authCompleted.current || force) {
        setUser(null);
        setError(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (sessionId) => {
    try {
      const response = await api.post('/auth/session', { session_id: sessionId });
      authCompleted.current = true;
      // Store session token in localStorage for mobile fallback
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data);
      setError(null);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Ignore logout errors
    } finally {
      authCompleted.current = false;
      localStorage.removeItem('session_token');
      sessionStorage.removeItem('pending_session_id');
      setUser(null);
    }
  }, []);

  const setUserFromCallback = useCallback((userData) => {
    authCompleted.current = true;
    setUser(userData);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Skip initial auth check during OAuth callback processing
    // AuthCallback component will handle authentication via login()
    const isOAuthCallback = window.location.hash?.includes('session_id=') ||
      window.location.pathname === '/auth/callback';

    if (isOAuthCallback) {
      // Set a safety timeout — if AuthCallback doesn't resolve in 15s,
      // try to recover with localStorage token
      const safetyTimeout = setTimeout(async () => {
        if (loading && !authCompleted.current) {
          const token = localStorage.getItem('session_token');
          if (token) {
            await checkAuth(true);
          } else {
            setLoading(false);
          }
        }
      }, 15000);
      return () => clearTimeout(safetyTimeout);
    }

    // Normal page load — authenticate from cookie or localStorage Bearer token
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAuth]);

  // Role helpers
  const isOwner = user?.role === 'owner' || user?.role === 'super_owner';
  const isSuperOwner = user?.role === 'super_owner';
  const isBaseOwner = user?.role === 'owner' && !isSuperOwner;
  const isInvestor = user?.role === 'investor';
  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isOwner,
    isSuperOwner,
    isBaseOwner,
    isInvestor,
    login,
    logout,
    checkAuth,
    setUserFromCallback,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
